import i18next from 'i18next';
import { useSnackbar } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';

interface ApiErrorShape {
  response?: { status?: number; data?: { code?: string; message?: string | string[] } };
  request?: unknown;
}

/** The HTTP status of a failed request, or null when there was no response. */
export function apiErrorStatus(err: unknown): number | null {
  return (err as ApiErrorShape)?.response?.status ?? null;
}

/** The machine-readable reason the API gave, when it gave one. */
function apiErrorCode(err: unknown): string | null {
  return (err as ApiErrorShape)?.response?.data?.code ?? null;
}

/**
 * What to tell the officer about a failed request, in their language.
 *
 * The API's own `message` is English and written for whoever reads the logs, so
 * it is no longer shown as-is. In order of preference:
 *
 * 1. a known `code` from the API (`gov.errors.code.*`) — it names the actual
 *    problem, such as an unserved notice, in words written for the officer;
 * 2. the screen's own fallback, when the caller gave one — it says what was
 *    being attempted, which a bare status cannot;
 * 3. a sentence for the HTTP status (`gov.errors.status.*`), or the network
 *    sentence when nothing came back at all.
 */
export function apiErrorMessage(err: unknown, fallback?: string): string {
  const code = apiErrorCode(err);
  if (code && i18next.exists(`gov.errors.code.${code}`)) {
    return i18next.t(`gov.errors.code.${code}`);
  }
  // The by-law workflow refusals keep their own namespace beside the workflow.
  if (code && i18next.exists(`gov.bylawWorkflow.errors.${code}`)) {
    return i18next.t(`gov.bylawWorkflow.errors.${code}`);
  }
  if (fallback) return fallback;
  const status = apiErrorStatus(err);
  if (status && i18next.exists(`gov.errors.status.${status}`)) {
    return i18next.t(`gov.errors.status.${status}`);
  }
  if (status === null && (err as ApiErrorShape)?.request) return i18next.t('gov.errors.network');
  return i18next.t('common.errors.generic');
}

/**
 * The confirmation every write owes the officer.
 *
 * Enforcement work is a sequence of small commitments — a checklist item ticked,
 * a notice generated, service recorded — and each one either landed or did not.
 * A screen that changes nothing visible on success is indistinguishable from one
 * that silently failed, and an officer who cannot tell will do it twice.
 *
 * Wired into the mutation hooks rather than the screens: there is one hook per
 * write and several screens per hook, so this is the only place that guarantees
 * every caller gets the same treatment.
 */
export function useMutationToast() {
  const { t } = useTranslation();
  const snackbar = useSnackbar();

  return {
    /** `key` is a full i18n key; `values` are its interpolations. */
    success(key: string, values?: Record<string, unknown>) {
      snackbar.success(t(key, values ?? {}));
    },
    /** A translated reason when the API coded one, otherwise the screen's own line. */
    error(err: unknown, fallbackKey: string) {
      snackbar.error(apiErrorMessage(err, t(fallbackKey)));
    },
  };
}
